import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTag, faSpinner, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { useValidateCoupons, useApplyCoupons } from '../../hooks/useCoupons'
import { CouponValidationResult, DiscountBreakdown } from '../../types/coupon'
import { makeNumbersFarsi } from '../../utils/farsi'
import { addThousandsSeparator } from '../../utils/format'

interface CouponInputProps {
  orderId: string
  orderSubtotal: number
  customerId?: string
  onCouponApplied: (updatedCart: any) => void
  appliedCoupons?: string[]
}

export const CouponInput = ({
  orderId,
  orderSubtotal,
  customerId,
  onCouponApplied,
  appliedCoupons = [],
}: CouponInputProps) => {
  const [couponCode, setCouponCode] = useState('')
  const [validationResults, setValidationResults] = useState<
    CouponValidationResult[]
  >([])
  const [discountPreview, setDiscountPreview] = useState<DiscountBreakdown | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const { validateCoupons, isValidating, validationError } =
    useValidateCoupons()
  const { applyCoupons, isApplying, applyError } = useApplyCoupons()

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null)
        setDiscountPreview(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const formatPrice = (value: number) => {
    const priceStr = value.toString().substring(0, value.toString().length - 1)
    return makeNumbersFarsi(addThousandsSeparator(priceStr))
  }

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      return
    }

    // Check if coupon is already applied
    if (appliedCoupons.includes(couponCode.trim().toUpperCase())) {
      setValidationResults([
        {
          code: couponCode,
          valid: false,
          errors: ['این کد تخفیف قبلا اعمال شده است'],
        },
      ])
      setDiscountPreview(null)
      return
    }

    // Validate the coupon first
    const validationResponse = await validateCoupons(
      [couponCode.trim().toUpperCase()],
      orderSubtotal,
      customerId,
    )

    if (!validationResponse) {
      setDiscountPreview(null)
      return
    }

    setValidationResults(validationResponse.validationResults)

    // If valid, show preview and then apply to order
    const isValid = validationResponse.validationResults.every((r) => r.valid)
    if (isValid) {
      // Show discount preview
      setDiscountPreview(validationResponse.discountBreakdown)
      
      // Apply to order
      const allCoupons = [...appliedCoupons, couponCode.trim().toUpperCase()]
      const result = await applyCoupons(orderId, allCoupons)

      if (result) {
        setSuccessMessage('کد تخفیف با موفقیت اعمال شد!')
        setCouponCode('')
        setValidationResults([])
        
        // Update the cart with new discount data
        onCouponApplied(result)
      }
    } else {
      setDiscountPreview(null)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleApplyCoupon()
    }
  }

  const renderValidationMessages = () => {
    if (validationResults.length === 0) return null

    return (
      <div className='mt-3'>
        {validationResults.map((result, index) => (
          <div
            key={index}
            className={`flex items-start p-3 rounded ${
              result.valid
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}
          >
            <FontAwesomeIcon
              icon={result.valid ? faCheckCircle : faTimesCircle}
              className='ml-2 mt-1'
            />
            <div className='flex-1'>
              {result.valid ? (
                <div>
                  <div className='font-semibold mb-2'>
                    کد تخفیف {result.code} معتبر است ✓
                  </div>
                  
                  {/* Show discount preview */}
                  {discountPreview && (
                    <div className='mt-3 space-y-2 text-sm'>
                      <div className='flex justify-between items-center py-1 border-b border-green-300 dark:border-green-700'>
                        <span>مبلغ اصلی:</span>
                        <span className='font-bold'>{formatPrice(discountPreview.appliedDiscounts[0]?.appliedTo || orderSubtotal)} تومان</span>
                      </div>
                      
                      {discountPreview.appliedDiscounts.map((discount, idx) => (
                        <div key={idx} className='flex justify-between items-center py-1'>
                          <span>
                            تخفیف ({discount.code}):
                            {discount.type === 'percentage' 
                              ? ` ${makeNumbersFarsi(discount.value)}%` 
                              : ''}
                          </span>
                          <span className='font-bold text-green-700 dark:text-green-300'>
                            - {formatPrice(discount.discountAmount)} تومان
                          </span>
                        </div>
                      ))}
                      
                      <div className='flex justify-between items-center py-2 border-t-2 border-green-400 dark:border-green-600 mt-2'>
                        <span className='font-bold text-base'>قابل پرداخت:</span>
                        <span className='font-bold text-lg text-green-700 dark:text-green-300'>
                          {formatPrice(discountPreview.finalAmount)} تومان
                        </span>
                      </div>
                      
                      <div className='text-xs text-center mt-2 opacity-75'>
                        این مبلغ پس از اعمال تخفیف نمایش داده می‌شود
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className='font-semibold mb-1'>
                    کد تخفیف {result.code} نامعتبر است:
                  </div>
                  <ul className='list-disc list-inside'>
                    {result.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className='border rounded-md p-4 my-4' style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
      <div className='flex items-center mb-3'>
        <FontAwesomeIcon
          icon={faTag}
          className='ml-2'
          style={{ color: 'var(--accent-primary)' }}
        />
        <h3 className='text-lg font-semibold'>کد تخفیف دارید؟</h3>
      </div>

      <div className='flex flex-col md:flex-row gap-2'>
        <input
          type='text'
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
          onKeyPress={handleKeyPress}
          placeholder='کد تخفیف را وارد کنید'
          className='input-theme flex-1 outline-none rounded border p-3 text-base'
          disabled={isValidating || isApplying}
          dir='ltr'
        />
        <button
          onClick={handleApplyCoupon}
          disabled={isValidating || isApplying || !couponCode.trim()}
          className='p-3 rounded text-white font-semibold flex items-center justify-center min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:opacity-90'
          style={{ backgroundColor: 'var(--accent-primary)' }}
        >
          {isValidating || isApplying ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className='ml-2' />
              در حال بررسی...
            </>
          ) : (
            'اعمال کد'
          )}
        </button>
      </div>

      {renderValidationMessages()}

      {successMessage && (
        <div className='mt-3 p-3 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 flex items-center'>
          <FontAwesomeIcon icon={faCheckCircle} className='ml-2' />
          {successMessage}
        </div>
      )}

      {(validationError || applyError) && (
        <div className='mt-3 p-3 rounded bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 flex items-center'>
          <FontAwesomeIcon icon={faTimesCircle} className='ml-2' />
          {validationError || applyError}
        </div>
      )}
    </div>
  )
}

